import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Col, Container, Row, Table, Button, Badge } from "react-bootstrap";
import { Marker } from "react-leaflet";
import MapComponent from "../components/MapCompnent";
import AdministrationIcon from "../assets/images/two_gears_rgb_red.svg";
import api from "../services/httpClient";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "../assets/styles/newAddress.scss";

// Fix domyslnej ikony Leaflet w React (brakujace ikony z node_modules)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const statusNames = {
  "-2": "Odrzucony przez Algolytics",
  "-1": "Odrzucony przez DHL",
  "1": "W trakcie analizy",
  "2": "Zgłoszony przez DHL",
  "3": "Zaakceptowany przez Algolytics",
};

const getStatusColor = (status) => {
  switch (status) {
    case -2: return "danger";
    case -1: return "secondary";
    case 1: return "warning";
    case 2: return "info";
    case 3: return "success";
    default: return "light";
  }
};

const NewAddressDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const response = await api.get(`/NewAddress/${id}`);
        const result = response.data?.data;
        if (result?.rows && result.rows.length > 0) {
          setDetails(result.rows[0]);
        }
      } catch (error) {
        console.error("Error fetching new address details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id]);

  const handleReportByDhl = async () => {
    if (!window.confirm("Czy na pewno chcesz zgłosić ten adres przez DHL?")) return;
    try {
      await api.post(`/NewAddress/${id}/ReportByDhl`);
      alert("Adres został zgłoszony przez DHL.");
      window.location.reload();
    } catch (error) {
      const msg = error.response?.data?.data?.message || "Wystąpił błąd podczas zgłaszania adresu.";
      alert(msg);
    }
  };

  const handleRejectByDhl = async () => {
    if (!window.confirm("Czy na pewno chcesz odrzucić ten adres?")) return;
    try {
      await api.post(`/NewAddress/${id}/RejectByDhl`);
      alert("Adres został odrzucony przez DHL.");
      window.location.reload();
    } catch (error) {
      const msg = error.response?.data?.data?.message || "Wystąpił błąd podczas odrzucania adresu.";
      alert(msg);
    }
  };

  if (loading) {
    return (
      <Container fluid className="mt-4">
        <p>Ładowanie...</p>
      </Container>
    );
  }

  if (!details) {
    return (
      <Container fluid className="mt-4">
        <p>Nie znaleziono adresu o ID {id}.</p>
        <Button variant="secondary" onClick={() => navigate("/administration/new-addresses")}>
          Powrot do listy
        </Button>
      </Container>
    );
  }

  // Współrzędne mapy: y = latitude, x = longitude
  const mapCenter = details.y && details.x ? [details.y, details.x] : [51.759, 19.458];
  const hasCoordinates = details.y !== 0 && details.x !== 0;

  // Przyciski akcji widoczne tylko gdy status == 1 (InAnalysis)
  const canChangeStatus = details.status === 1;
  const canEdit = details.status === 1;

  return (
    <div className="new-address-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <img src={AdministrationIcon} alt="New Address Icon" style={{ width: "30px", height: "30px" }} />
          </Col>
          <Col>
            <h3 style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
              Szczegóły adresu #{details.id}
            </h3>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            {canChangeStatus && (
              <>
                <Button variant="success" onClick={handleReportByDhl}>
                  Zgłoś adres
                </Button>
                <Button variant="danger" onClick={handleRejectByDhl}>
                  Odrzuć adres
                </Button>
              </>
            )}
            {canEdit && (
              <Button variant="primary" onClick={() => navigate(`/administration/new-addresses/edit/${id}`)}>
                Edytuj
              </Button>
            )}
            <Button variant="secondary" onClick={() => navigate("/administration/new-addresses")}>
              Powrót do listy
            </Button>
          </Col>
        </Row>

        <Row>
          {/* Lewa kolumna — dane adresu */}
          <Col md={6}>
            <Table bordered size="sm">
              <tbody>
                <tr><td><strong>ID</strong></td><td>{details.id}</td></tr>
                <tr>
                  <td><strong>Status</strong></td>
                  <td>
                    <Badge bg={getStatusColor(details.status)}>
                      {details.statusName || statusNames[details.status]}
                    </Badge>
                  </td>
                </tr>
                <tr><td><strong>Województwo</strong></td><td>{details.province}</td></tr>
                <tr><td><strong>Miejscowość</strong></td><td>{details.cityName}</td></tr>
                <tr><td><strong>Kod pocztowy</strong></td><td>{details.postcode}</td></tr>
                <tr><td><strong>Typ kodu</strong></td><td>{details.type}</td></tr>
                <tr><td><strong>Atrybut</strong></td><td>{details.attribute}</td></tr>
                <tr><td><strong>Ulica</strong></td><td>{details.street}</td></tr>
                <tr><td><strong>Numer</strong></td><td>{details.number}</td></tr>
                <tr><td><strong>Budynek</strong></td><td>{details.building}</td></tr>
                <tr><td><strong>x (longitude)</strong></td><td>{details.x}</td></tr>
                <tr><td><strong>y (latitude)</strong></td><td>{details.y}</td></tr>
                <tr><td><strong>Data dodania</strong></td><td>{details.dateAdd}</td></tr>
                {details.dateUpdated && <tr><td><strong>Data aktualizacji</strong></td><td>{details.dateUpdated}</td></tr>}
              </tbody>
            </Table>
          </Col>

          {/* Prawa kolumna — mapa */}
          <Col md={6}>
            {hasCoordinates ? (
              <MapComponent center={mapCenter} zoom={15} style={{ height: "500px", width: "100%" }}>
                <Marker position={mapCenter} />
              </MapComponent>
            ) : (
              <div className="d-flex align-items-center justify-content-center" style={{ height: "500px", backgroundColor: "#f0f0f0" }}>
                <p>Brak współrzędnych do wyświetlenia na mapie.</p>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default NewAddressDetails;
